import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { CartLine } from '../lib/shop/checkout'

export interface CartItem {
  id: string
  title: string
  handle: string
  variantId: string
  price: number
  quantity: number
  image?: string
}

interface CartState {
  items: CartItem[]
  /** Shopify Cart GID - persisted so we can reuse the cart across page loads. */
  shopifyCartId: string | null
  /**
   * Maps CartItem.variantId (numeric string) to the Shopify cart line GID.
   * Required to call cartLinesUpdate / cartLinesRemove on specific lines.
   * Replaced wholesale each time a cart mutation returns updated lines.
   */
  lineIdMap: Record<string, string>
  addItem: (item: Omit<CartItem, 'quantity'>) => void
  removeItem: (variantId: string) => void
  updateQuantity: (variantId: string, quantity: number) => void
  clearCart: () => void
  setShopifyCartId: (id: string | null) => void
  /**
   * Replaces the entire lineIdMap with the lines returned by any cart mutation.
   * Passing an empty array clears the map.
   */
  setLineIds: (lines: CartLine[]) => void
  cartCount: () => number
  cartTotal: () => number
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      shopifyCartId: null,
      lineIdMap: {},

      addItem: (item) =>
        set((state) => {
          const existing = state.items.find((i) => i.variantId === item.variantId)
          if (existing) {
            return {
              items: state.items.map((i) =>
                i.variantId === item.variantId
                  ? { ...i, quantity: i.quantity + 1 }
                  : i
              ),
            }
          }
          return { items: [...state.items, { ...item, quantity: 1 }] }
        }),

      removeItem: (variantId) =>
        set((state) => ({
          items: state.items.filter((i) => i.variantId !== variantId),
        })),

      updateQuantity: (variantId, quantity) =>
        set((state) => ({
          items:
            quantity <= 0
              ? state.items.filter((i) => i.variantId !== variantId)
              : state.items.map((i) =>
                  i.variantId === variantId ? { ...i, quantity } : i
                ),
        })),

      clearCart: () => set({ items: [], shopifyCartId: null, lineIdMap: {} }),

      setShopifyCartId: (id) =>
        set((state) => ({
          shopifyCartId: id,
          // Clear stale line IDs when the cart is invalidated.
          lineIdMap: id === null ? {} : state.lineIdMap,
        })),

      setLineIds: (lines) =>
        set(() => ({
          lineIdMap: Object.fromEntries(lines.map((l) => [l.variantId, l.lineId])),
        })),

      cartCount: () => get().items.reduce((sum, i) => sum + i.quantity, 0),
      cartTotal: () =>
        get().items.reduce((sum, i) => sum + i.price * i.quantity, 0),
    }),
    { name: 'helin-cart' }
  )
)
